const BASE62_ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

const TIMEFRAME_SECONDS = {
  "5M": 5 * 60,
  "15M": 15 * 60,
  "4H": 4 * 60 * 60,
  "1D": 24 * 60 * 60,
  "1W": 7 * 24 * 60 * 60,
};

const LOOKBACK_SECONDS = {
  "24h": 24 * 60 * 60,
  "7d": 7 * 24 * 60 * 60,
  "30d": 30 * 24 * 60 * 60,
};

const MAX_ANCHOR_CACHE_SIZE = 2000;
const drawingAnchorPriceCache = new Map();

const DIRECTION_STYLE = {
  bull: {
    color: "rgba(74, 222, 128, 0.88)",
    prefix: "▲",
  },
  bear: {
    color: "rgba(248, 113, 113, 0.88)",
    prefix: "▼",
  },
  neutral: {
    color: "rgba(251, 191, 36, 0.88)",
    prefix: "•",
  },
};

export const normalizeEventTimeframe = (timeframe) =>
  String(timeframe || "").replace(/^_/, "").toUpperCase();

const base62Decode = (encoded) => {
  if (!encoded) return "";

  let value = 0n;
  for (const character of encoded) {
    const index = BASE62_ALPHABET.indexOf(character);
    if (index < 0) return "";
    // eslint-disable-next-line no-undef
    value = value * 62n + BigInt(index);
  }

  const bytes = [];
  while (value > 0) {
    bytes.unshift(Number(value % 256n));
    value /= 256n;
  }
  return new TextDecoder().decode(new Uint8Array(bytes));
};

const decodeEventDrawings = (encoded) => {
  try {
    const decoded = base62Decode(encoded);
    return decoded ? JSON.parse(decoded) : [];
  } catch {
    return [];
  }
};

const eventAnchorPrice = (event) => {
  const explicitPrice = Number(event?.price ?? event?.trade?.entry);
  if (Number.isFinite(explicitPrice) && explicitPrice > 0) {
    return explicitPrice;
  }

  const cacheKey = String(event?.draw || "");
  if (cacheKey && drawingAnchorPriceCache.has(cacheKey)) {
    return drawingAnchorPriceCache.get(cacheKey);
  }

  const drawings = decodeEventDrawings(event?.draw);
  const eventTime = Number(event?.ts) || 0;
  const candidates = [];

  drawings.forEach((drawing) => {
    const shape = String(drawing?.options?.shape || "").toLowerCase();
    const shapePriority = shape.includes("arrow") ? 0 : shape === "text" ? 1 : 2;
    (drawing?.point || []).forEach((point) => {
      const price = Number(point?.price);
      if (!Number.isFinite(price)) return;
      const time = Number(point?.time) || eventTime;
      candidates.push({
        time,
        price,
        distance: Math.abs(time - eventTime),
        shapePriority,
      });
    });
  });

  candidates.sort((left, right) =>
    left.shapePriority - right.shapePriority || left.distance - right.distance
  );
  const price = candidates[0]?.price ?? null;
  if (cacheKey) {
    drawingAnchorPriceCache.set(cacheKey, price);
    if (drawingAnchorPriceCache.size > MAX_ANCHOR_CACHE_SIZE) {
      drawingAnchorPriceCache.delete(drawingAnchorPriceCache.keys().next().value);
    }
  }
  return price;
};

const chartCandleStart = (timestamp, timeframe) => {
  const seconds = TIMEFRAME_SECONDS[normalizeEventTimeframe(timeframe)] || TIMEFRAME_SECONDS["15M"];
  const time = Number(timestamp) || 0;

  if (normalizeEventTimeframe(timeframe) === "1W") {
    const mondayOffset = 3 * 24 * 60 * 60;
    return Math.floor((time + mondayOffset) / seconds) * seconds - mondayOffset;
  }
  return Math.floor(time / seconds) * seconds;
};

const eventDirection = (event) => {
  if (event?.bull && !event?.bear) return "bull";
  if (event?.bear && !event?.bull) return "bear";
  return "neutral";
};

const eventLabel = (sectionTitle, event) => {
  const source = String(sectionTitle || event?.name || "Event")
    .replace(/^_?(5M|15M|4H|1D|1W)\s+/i, "")
    .replace(/\s+/g, " ")
    .trim();
  return source || "Event";
};

const compactGroupLabel = (timeframe, events) => {
  const shorten = (label) => label.length > 22 ? `${label.slice(0, 21)}…` : label;
  const labels = [...new Set(events.map((event) => shorten(event.label)))];
  const visible = labels.slice(0, 2);
  const hiddenCount = Math.max(0, events.length - visible.length);
  return `${normalizeEventTimeframe(timeframe)} ${visible.join(" · ")}${hiddenCount ? ` · +${hiddenCount}` : ""}`;
};

const flattenHistoryEvents = (history) => {
  const events = [];
  (history?.sections || []).forEach((section) => {
    const sectionTitle = String(section?.title || "").trim();
    (section?.events || []).forEach((event) => {
      if (!event?.cur) return;
      events.push({
        ...event,
        sectionTitle,
        label: eventLabel(sectionTitle, event),
      });
    });
  });
  return events;
};

const markerDrawing = (group, eventTimeframe) => {
  const style = DIRECTION_STYLE[group.direction];
  const prices = group.events
    .map((event) => event.anchorPrice)
    .filter((price) => Number.isFinite(price));
  let price = null;
  if (prices.length) {
    if (group.direction === "bull") {
      price = Math.min(...prices) * 0.997;
    } else if (group.direction === "bear") {
      price = Math.max(...prices) * 1.003;
    } else {
      price = prices.reduce((sum, current) => sum + current, 0) / prices.length;
    }
  }
  const text = `${style.prefix} ${compactGroupLabel(eventTimeframe, group.events)}`;

  if (price == null) {
    return null;
  }

  return {
    overlayKey: `${group.time}|${group.direction}`,
    overlaySignature: `${text}|${Number(price).toPrecision(12)}`,
    point: [{ time: group.time, price }],
    options: {
      shape: "text",
      text,
      disableSelection: false,
      disableSave: true,
      lock: false,
      overrides: {
        color: style.color,
        textcolor: style.color,
        fontsize: 10,
        bold: false,
        italic: false,
        text,
      },
      zOrder: "top",
    },
  };
};

export const buildHistoricalEventOverlay = ({
  history,
  currency,
  eventTimeframe,
  chartTimeframe,
  lookback,
  now = Math.floor(Date.now() / 1000),
}) => {
  const normalizedCurrency = String(currency || "").toUpperCase();
  const normalizedEventTimeframe = normalizeEventTimeframe(eventTimeframe);
  const lookbackSeconds = LOOKBACK_SECONDS[lookback];
  const earliestTime = lookbackSeconds ? now - lookbackSeconds : 0;

  const matchingEvents = flattenHistoryEvents(history)
    .filter((event) => String(event.cur || "").toUpperCase() === normalizedCurrency)
    .filter((event) => normalizeEventTimeframe(event.tf) === normalizedEventTimeframe)
    .filter((event) => Number(event.ts) >= earliestTime)
    .map((event) => ({
      ...event,
      anchorPrice: eventAnchorPrice(event),
    }));

  const groups = new Map();
  matchingEvents.forEach((event) => {
    const time = chartCandleStart(event.ts, chartTimeframe);
    const direction = eventDirection(event);
    const key = `${time}|${direction}`;
    if (!groups.has(key)) {
      groups.set(key, { time, direction, events: [] });
    }
    groups.get(key).events.push(event);
  });

  const drawings = [...groups.values()]
    .sort((left, right) => left.time - right.time)
    .map((group) => markerDrawing(group, normalizedEventTimeframe))
    .filter(Boolean);

  return {
    drawings,
    eventCount: matchingEvents.length,
    markerCount: drawings.length,
  };
};
